import React, { useState, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { ArrowLeft, BarChart3, PieChart, TrendingUp, Filter, Search, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const CellStatus = () => {
  const [selectedCell, setSelectedCell] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedTask, setSelectedTask] = useState(''); // Single task selection
  const [hoveredSegment, setHoveredSegment] = useState(null); // {taskNo, status}
  const [hoveredCellIndex, setHoveredCellIndex] = useState(null); // Track hovered cell for visual feedback
  const chartRef = useRef(null); // Ref for the chart container

  // Handler functions for CSP compliance
  const handleCellChange = (e) => {
    setSelectedCell(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTaskFilterChange = (e) => {
    setSelectedTask(e.target.value);
  };

  const handleClearTaskFilter = () => {
    setSelectedTask('');
  };

  const handleChartClick = (data, index) => {
    if (data && filteredChartData[index] && filteredChartData[index].cellId) {
      setSelectedCell(String(filteredChartData[index].cellId));
    }
  };

  const handleBarClick = (data, index) => {
    if (data && data.cellId) {
      setSelectedCell(String(data.cellId));
    } else if (filteredChartData[index] && filteredChartData[index].cellId) {
      setSelectedCell(String(filteredChartData[index].cellId));
    }
  };

  // Fetch cells for dropdown
  const { data: cells } = useQuery(
    ['cells'],
    async () => {
      const response = await axios.get('/api/masters/cells');
      return response.data;
    }
  );

  // Fetch all cells task status for bar chart (when no cell is selected)
  const { data: allCellsData, isLoading: isLoadingAllCells, error: allCellsError } = useQuery(
    ['all-cells-status'],
    async () => {
      const response = await axios.get('/api/status/cells/all');
      return response.data;
    },
    {
      enabled: !selectedCell,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  // Fetch cell status data
  const { data: cellStatus, isLoading } = useQuery(
    ['cell-status', selectedCell],
    async () => {
      if (!selectedCell) return null;
      const response = await axios.get(`/api/status/cell/${selectedCell}`);
      return response.data;
    },
    {
      enabled: !!selectedCell
    }
  );

  // Get status background color for badges (matching bar chart colors)
  const getStatusBgColor = (status) => {
    if (!status) return '#e5e7eb'; // gray-200
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower === 'completed') {
      return '#d1fae5'; // green-100 (lighter shade of #10b981)
    } else if (statusLower === 'in progress') {
      return '#f3e8ff'; // purple-100 (lighter shade of rgb(204, 93, 232))
    } else if (statusLower === 'on hold') {
      return '#fed7aa'; // orange-200 (lighter shade of #F99601)
    } else if (statusLower === 'not started') {
      return '#f3f4f6'; // gray-100 (lighter shade of #9ca3af)
    }
    return '#f3f4f6';
  };

  // Get status text color for badges (matching bar chart colors)
  const getStatusTextColor = (status) => {
    if (!status) return '#4b5563'; // gray-600
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower === 'completed') {
      return '#059669'; // green-600 (darker shade of #10b981)
    } else if (statusLower === 'in progress') {
      return '#9333ea'; // purple-600 (darker shade of rgb(204, 93, 232))
    } else if (statusLower === 'on hold') {
      return '#ea580c'; // orange-600 (darker shade of #F99601)
    } else if (statusLower === 'not started') {
      return '#4b5563'; // gray-600 (darker shade of #9ca3af)
    }
    return '#4b5563';
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower === 'completed') {
      return 'text-green-600 bg-green-100'; // Green #10b981
    } else if (statusLower === 'in progress') {
      return 'text-purple-600 bg-purple-100'; // Purple rgb(204, 93, 232)
    } else if (statusLower === 'on hold') {
      return 'text-orange-600 bg-orange-100'; // Orange #F99601
    } else if (statusLower === 'not started') {
      return 'text-gray-600 bg-gray-100'; // Grey #9ca3af
    }
    return 'text-gray-600 bg-gray-100';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '✓';
      case 'in progress': return '';
      case 'on hold': return '⏸';
      case 'not started': return '○';
      default: return '○';
    }
  };

  // Status colors as defined by user
  const getStatusBarColor = (status) => {
    if (!status) return '#9ca3af';
    // Normalize status: lowercase, trim, and replace hyphens with spaces for consistent matching
    const normalized = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (normalized === 'completed') {
      return '#10b981'; // Green
    } else if (normalized === 'in progress') {
      return 'rgb(204, 93, 232)'; // Purple/Magenta
    } else if (normalized === 'on hold') {
      return '#F99601'; // Orange
    } else if (normalized === 'not started') {
      return '#9ca3af'; // Greyish
    }
    return '#9ca3af'; // Default greyish
  };

  // Task colors for legend (excluding status colors: green, grey, rgb(204, 93, 232), #F99601)
  const getTaskColor = (taskNo) => {
    const taskColors = {
      1: '#f97316', // Orange (distinct)
      2: '#8b5cf6', // Purple (different from status purple)
      3: '#ec4899', // Pink
      4: '#06b6d4', // Cyan
      5: '#3b82f6', // Blue
      6: '#14b8a6', // Teal
      7: '#f43f5e', // Rose
      8: '#a855f7', // Violet
    };
    return taskColors[taskNo] || '#f97316'; // Default orange
  };

  // Prepare chart data for bar graph
  const chartData = useMemo(() => {
    if (!allCellsData || allCellsData.length === 0) return [];

    // Group data by cell
    const cellMap = new Map();
    
    allCellsData.forEach(item => {
      const cellKey = `${item.cell_id}`;
      const cellLabel = `Cell ${item.cell_no} (${item.state_name})`;
      
      if (!cellMap.has(cellKey)) {
        cellMap.set(cellKey, {
          cellId: item.cell_id,
          cellName: cellLabel,
          cellNo: item.cell_no,
          stateName: item.state_name,
          tasks: []
        });
      }
      
      const cellData = cellMap.get(cellKey);
      // Add task if not already added
      const taskExists = cellData.tasks.some(t => t.taskNo === item.task_no);
      if (!taskExists) {
        cellData.tasks.push({
          taskNo: item.task_no,
          taskName: item.task_name,
          status: item.status,
          progressPercentage: item.progress_percentage || 0
        });
      }
    });

    // Convert to chart format - create entries for each cell with all tasks
    const chartDataArray = [];
    
    cellMap.forEach((cellData) => {
      // Sort tasks by task number
      cellData.tasks.sort((a, b) => a.taskNo - b.taskNo);
      
      const chartEntry = {
        cellName: cellData.cellName, // Full name for tooltip
        cellShortName: `Cell ${cellData.cellNo}`, // Short name for X-axis
        cellId: cellData.cellId,
        cellNo: cellData.cellNo // Store cell number for sorting
      };

      // Add each task as a separate field with progress percentage
      cellData.tasks.forEach(task => {
        const taskKey = `Task ${task.taskNo}`;
        // Use progress percentage (0-100) instead of status value
        chartEntry[taskKey] = task.progressPercentage || 0;
        chartEntry[`${taskKey}_status`] = task.status;
        chartEntry[`${taskKey}_progress`] = task.progressPercentage || 0;
        chartEntry[`${taskKey}_color`] = getStatusBarColor(task.status);
        chartEntry[`${taskKey}_baseColor`] = getTaskColor(task.taskNo);
      });

      chartDataArray.push(chartEntry);
    });

    // Sort by cell number in ascending order (1, 2, 3, ...)
    chartDataArray.sort((a, b) => a.cellNo - b.cellNo);

    return chartDataArray;
  }, [allCellsData]);

  // Get all unique task numbers for bars
  const allTaskKeys = useMemo(() => {
    if (!allCellsData || allCellsData.length === 0) return [];
    const tasks = new Set();
    allCellsData.forEach(item => {
      tasks.add(`Task ${item.task_no}`);
    });
    return Array.from(tasks).sort((a, b) => {
      const numA = parseInt(a.replace('Task ', ''));
      const numB = parseInt(b.replace('Task ', ''));
      return numA - numB;
    });
  }, [allCellsData]);

  // Filter task keys based on selected task
  const taskKeys = useMemo(() => {
    if (!selectedTask) {
      return allTaskKeys; // Show all tasks if none selected
    }
    return [selectedTask]; // Show only selected task
  }, [allTaskKeys, selectedTask]);

  // Filter chart data to only include selected task
  const filteredChartData = useMemo(() => {
    if (!selectedTask) {
      return chartData; // Show all tasks if none selected
    }
    return chartData.map(cellEntry => {
      const filteredEntry = { ...cellEntry };
      // Remove task data that's not the selected task
      allTaskKeys.forEach(taskKey => {
        if (taskKey !== selectedTask) {
          delete filteredEntry[taskKey];
          delete filteredEntry[`${taskKey}_status`];
          delete filteredEntry[`${taskKey}_color`];
          delete filteredEntry[`${taskKey}_baseColor`];
        }
      });
      return filteredEntry;
    });
  }, [chartData, selectedTask, allTaskKeys]);

  // Deduplicate detailed progress to get only unique subtasks (latest status)
  const uniqueDetailedProgress = useMemo(() => {
    if (!cellStatus?.detailedProgress) return [];
    
    const uniqueSubtasksMap = new Map();
    cellStatus.detailedProgress.forEach(item => {
      const subtaskKey = `${item.task_no}_${item.subtask_no}`;
      if (!uniqueSubtasksMap.has(subtaskKey)) {
        uniqueSubtasksMap.set(subtaskKey, item);
      } else {
        const existingItem = uniqueSubtasksMap.get(subtaskKey);
        const existingDate = new Date(existingItem.last_updated || 0);
        const currentDate = new Date(item.last_updated || 0);
        // Keep the one with the latest date
        if (currentDate > existingDate) {
          uniqueSubtasksMap.set(subtaskKey, item);
        }
      }
    });
    
    return Array.from(uniqueSubtasksMap.values());
  }, [cellStatus?.detailedProgress]);

  // Process task/subtask breakdown data for stacked bar chart
  const taskSubtaskBreakdown = useMemo(() => {
    if (!uniqueDetailedProgress || uniqueDetailedProgress.length === 0) return [];
    
    const taskMap = new Map();
    
    // Count unique subtasks only (already deduplicated)
    uniqueDetailedProgress.forEach(item => {
      const taskKey = `Task ${item.task_no}`;
      
      if (!taskMap.has(taskKey)) {
        taskMap.set(taskKey, {
          taskNo: item.task_no,
          taskName: item.task_name,
          completed: 0,
          inProgress: 0,
          notStarted: 0,
          total: 0,
          completedSubtasks: [],
          inProgressSubtasks: [],
          notStartedSubtasks: []
        });
      }
      
      const taskData = taskMap.get(taskKey);
      taskData.total++;
      
      const statusLower = (item.status || '').toLowerCase().trim().replace(/-/g, ' ');
      const subtaskName = `Subtask ${item.subtask_no}: ${item.subtask_name}`;
      
      if (statusLower === 'completed') {
        taskData.completed++;
        taskData.completedSubtasks.push(subtaskName);
      } else if (statusLower === 'in progress' || statusLower === 'on hold') {
        // Treat "On Hold" as "In Progress" for tasks
        taskData.inProgress++;
        taskData.inProgressSubtasks.push(subtaskName);
      } else {
        taskData.notStarted++;
        taskData.notStartedSubtasks.push(subtaskName);
      }
    });
    
    return Array.from(taskMap.values()).sort((a, b) => a.taskNo - b.taskNo);
  }, [uniqueDetailedProgress]);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Get full cell name from payload data
      const fullCellName = payload[0]?.payload?.cellName || label;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-xl shadow-2xl backdrop-blur-sm">
          <p className="font-bold text-gray-900 mb-3 text-base border-b border-gray-200 pb-2">{fullCellName}</p>
          <div className="space-y-2">
            {payload.map((entry, index) => {
              const status = entry.payload[`${entry.dataKey}_status`];
              const progressValue = entry.payload[`${entry.dataKey}_progress`] || entry.payload[entry.dataKey] || 0;
              const progress = typeof progressValue === 'number' ? progressValue : parseFloat(progressValue) || 0;
              const taskColor = entry.payload[`${entry.dataKey}_baseColor`] || getTaskColor(parseInt(entry.dataKey.replace('Task ', '')));
              const statusColor = getStatusBarColor(status);
              const isInProgress = status?.toLowerCase() === 'in progress';
              // Convert task color to rgba for background
              let bgColor = taskColor + '20';
              if (taskColor.startsWith('rgb')) {
                // Handle rgb format
                const rgbMatch = taskColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (rgbMatch) {
                  bgColor = `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, 0.15)`;
                }
              } else if (taskColor.startsWith('#')) {
                // Handle hex format
                const rgb = taskColor.match(/\w\w/g)?.map(x => parseInt(x, 16));
                if (rgb) {
                  bgColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.15)`;
                }
              }
              return (
                <div key={index} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full shadow-sm dynamic-bg-color" 
                    style={{ '--dynamic-bg-color': taskColor }}
                  ></div>
                  <span className="text-sm font-semibold text-gray-700">{entry.dataKey}:</span>
                  <span 
                    className="text-sm font-medium px-2 py-0.5 rounded dynamic-bg-text-color"
                    style={{ 
                      '--dynamic-bg-color': bgColor,
                      '--dynamic-text-color': statusColor
                    }}
                  >
                    {status}
                  </span>
                  {isInProgress && (
                    <span className="text-xs font-semibold text-gray-700">
                      {progress.toFixed(2)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  // Function to download chart as PDF
  const downloadChartAsPDF = async () => {
    if (!chartRef.current) return;

    try {
      // Show loading state (optional)
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const imgWidth = 297; // A4 width in mm (landscape)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let yPosition = 15;
      
      // Add title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('All Cells Task Status Overview', 148.5, yPosition, { align: 'center' });
      yPosition += 7;
      
      // Add date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 148.5, yPosition, { align: 'center' });
      yPosition += 10;
      
      // Add detailed task breakdown section
      if (yPosition > 160) {
        pdf.addPage('landscape', 'a4');
        yPosition = 15;
      }
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Detailed Task Status by Cell', 15, yPosition);
      yPosition += 8;
      
      // Group tasks by cell for detailed view
      const cellTaskMap = new Map();
      if (allCellsData && allCellsData.length > 0) {
        allCellsData.forEach(item => {
          const cellKey = `Cell ${item.cell_no} (${item.state_name})`;
          if (!cellTaskMap.has(cellKey)) {
            cellTaskMap.set(cellKey, {
              cellNo: item.cell_no,
              cellName: cellKey,
              tasks: []
            });
          }
          cellTaskMap.get(cellKey).tasks.push({
            taskNo: item.task_no,
            taskName: item.task_name,
            status: item.status,
            progress: typeof item.progress_percentage === 'number' 
              ? item.progress_percentage 
              : parseFloat(item.progress_percentage) || 0
          });
        });
      }
      
      // Display task details for each cell - sort by cell number in ascending order
      const sortedCells = Array.from(cellTaskMap.values())
        .sort((a, b) => a.cellNo - b.cellNo)
        .map(cell => [cell.cellName, cell.tasks]);
      sortedCells.forEach(([cellName, tasks]) => {
        if (yPosition > 180) {
          pdf.addPage('landscape', 'a4');
          yPosition = 15;
        }
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(cellName, 15, yPosition);
        yPosition += 5;
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        tasks.sort((a, b) => a.taskNo - b.taskNo).forEach(task => {
          if (yPosition > 185) {
            pdf.addPage('landscape', 'a4');
            yPosition = 15;
          }
          
          const taskColor = getTaskColor(task.taskNo);
          const statusColor = getStatusBarColor(task.status);
          const isInProgress = task.status?.toLowerCase() === 'in progress';
          
          // Task number with color indicator (simulated with text)
          pdf.setTextColor(0, 0, 0); // Reset to black
          pdf.text(`Task ${task.taskNo}:`, 20, yPosition);
          
          // Task name (truncated if needed)
          const displayName = task.taskName.length > 60 
            ? task.taskName.substring(0, 57) + '...' 
            : task.taskName;
          pdf.text(displayName, 50, yPosition, { maxWidth: 180 });
          
          // Status and progress
          const statusText = isInProgress 
            ? `${task.status} ${task.progress.toFixed(2)}%`
            : task.status;
          
          // Set status color
          if (statusColor.startsWith('#')) {
            const rgb = statusColor.match(/\w\w/g)?.map(x => parseInt(x, 16));
            if (rgb) {
              pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
            }
          }
          
          pdf.text(statusText, 240, yPosition);
          pdf.setTextColor(0, 0, 0); // Reset to black
          
          yPosition += 4;
        });
        
        yPosition += 3; // Space between cells
      });
      
      // Add chart image on new page
      pdf.addPage('landscape', 'a4');
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Visual Chart', 148.5, 15, { align: 'center' });
      
      // Add chart image
      const chartY = 25;
      const chartHeight = Math.min(imgHeight - 20, 150); // Limit height to fit on page
      pdf.addImage(imgData, 'PNG', 10, chartY, imgWidth - 20, chartHeight);
      
      // Save PDF
      pdf.save(`Cell_Status_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Cell Wise Status</h1>
      </div>

      {/* Filters and Controls */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Select Cell:</label>
            <select
              value={selectedCell}
              onChange={handleCellChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Choose a cell...</option>
              {cells?.map((cell) => (
                <option key={cell.cell_id} value={cell.cell_id}>
                  Cell {cell.cell_no} ({cell.state_name})
                </option>
              ))}
            </select>
          </div>

          {selectedCell && (
            <>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="Completed">Completed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Not Started">Not Started</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bar Chart - Show when no cell is selected */}
      {!selectedCell && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              All Cells Task Status Overview
            </h3>
            {/* Download PDF and Task Filter */}
            <div className="flex items-center space-x-4">
              <button
                onClick={downloadChartAsPDF}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Download className="h-4 w-4" />
                <span>Download PDF</span>
              </button>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm font-medium text-gray-700">Filter Task:</label>
              <select
                value={selectedTask}
                onChange={handleTaskFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[150px]"
              >
                <option value="">All Tasks</option>
                {allTaskKeys.map((taskKey) => (
                  <option key={taskKey} value={taskKey}>
                    {taskKey}
                  </option>
                ))}
              </select>
              {selectedTask && (
                <button
                  onClick={handleClearTaskFilter}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
              </div>
            </div>
          </div>

          {isLoadingAllCells ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
          ) : allCellsError ? (
            <div className="flex items-center justify-center h-64 text-red-500">
              <div>
                <p className="font-semibold">Error loading chart data</p>
                <p className="text-sm mt-2">{allCellsError.message || 'Failed to fetch data'}</p>
              </div>
            </div>
          ) : filteredChartData.length > 0 ? (
            <div ref={chartRef}>
              {/* Status and Task Legends - Top */}
              <div className="mb-6 pb-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-lg p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Status Colors Legend */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-2"></div>
                      <p className="text-sm font-bold text-gray-800">Status</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': '#10b981' }}></div>
                        <span className="text-sm font-semibold text-gray-700">Completed</span>
                      </div>
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': 'rgb(204, 93, 232)' }}></div>
                        <span className="text-sm font-semibold text-gray-700">In Progress</span>
                      </div>
                      <div className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="w-5 h-5 rounded-full shadow-md border-2 border-white dynamic-bg-color" style={{ '--dynamic-bg-color': '#9ca3af' }}></div>
                        <span className="text-sm font-semibold text-gray-700">Not Started</span>
                      </div>
                    </div>
                  </div>

                  {/* Task Colors Legend */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full mr-2"></div>
                      <p className="text-sm font-bold text-gray-800">Tasks</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {taskKeys.map((taskKey) => {
                        const taskNo = parseInt(taskKey.replace('Task ', ''));
                        const baseColor = getTaskColor(taskNo);
                        return (
                          <div key={taskKey} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50 transition-colors">
                            <div 
                              className="w-5 h-5 rounded shadow-md border-2 border-white dynamic-bg-color" 
                              style={{ '--dynamic-bg-color': baseColor }}
                            ></div>
                            <span className="text-sm font-semibold text-gray-700">{taskKey}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full overflow-x-auto">
                <div className="chart-container-min-width">
                  <ResponsiveContainer width="100%" height={600}>
                  <BarChart
                    data={filteredChartData}
                    margin={{ top: 30, right: 50, left: 50, bottom: 80 }}
                    barCategoryGap="6%"
                    barGap={4}
                    onClick={handleChartClick}
                  >
                    <defs>
                      {taskKeys.map((taskKey) => {
                        const taskNo = parseInt(taskKey.replace('Task ', ''));
                        const baseColor = getTaskColor(taskNo);
                        const rgb = baseColor.match(/\w\w/g)?.map(x => parseInt(x, 16));
                        if (!rgb) return null;
                        return (
                          <linearGradient key={taskKey} id={`gradient-${taskNo}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={baseColor} stopOpacity={1} />
                            <stop offset="100%" stopColor={`rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.7)`} stopOpacity={0.8} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                    <XAxis 
                      dataKey="cellName" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }}
                      tickMargin={8}
                      stroke="#9ca3af"
                      strokeWidth={1}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      ticks={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]}
                      tickFormatter={(value) => {
                        return `${value}%`;
                      }}
                      tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }}
                      stroke="#9ca3af"
                      strokeWidth={1}
                      width={60}
                      label={{ value: 'Progress %', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#4b5563', fontWeight: 'bold' } }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {taskKeys.map((taskKey) => {
                      const taskNo = parseInt(taskKey.replace('Task ', ''));
                      const baseColor = getTaskColor(taskNo);
                      return (
                        <Bar 
                          key={taskKey}
                          dataKey={taskKey}
                          name={taskKey}
                          barSize={50}
                          radius={[8, 8, 0, 0]}
                          fill={baseColor}
                          stroke={baseColor}
                          strokeWidth={0.5}
                          className="bar-chart-style"
                          onClick={handleBarClick}
                        >
                          {filteredChartData.map((entry, idx) => {
                            // Use task color instead of status color
                            const baseColorKey = `${taskKey}_baseColor`;
                            const isHovered = hoveredCellIndex === idx;
                            return (
                              <Cell 
                                key={`${taskKey}-cell-${idx}`} 
                                fill={entry[baseColorKey] || baseColor}
                                className="cell-transition"
                                style={{ 
                                  opacity: isHovered ? 0.8 : 1
                                }}
                                onMouseEnter={() => setHoveredCellIndex(idx)}
                                onMouseLeave={() => setHoveredCellIndex(null)}
                              />
                            );
                          })}
                        </Bar>
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
        </div>
      )}

      {selectedCell && (
        <>
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => {
                setSelectedCell('');
                setStatusFilter('');
                setSearchTerm('');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to All Cells
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Task Status Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Task Status Cards */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Task Status Overview
                  </h3>
                  <div className="space-y-3">
                    {cellStatus?.taskStatus?.map((task) => (
                      <div key={task.task_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            <span style={{ color: getTaskColor(task.task_no), fontWeight: '600' }}>Task {task.task_no}</span>: {task.task_name}
                          </h4>
                          <div className="flex items-center gap-2">
                            {task.latest_status?.toLowerCase() === 'in progress' && (
                              <span className="text-xs font-semibold text-gray-700">
                                {task.completion_percentage || 0}%
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.latest_status)}`}>
                              {getStatusIcon(task.latest_status)} {task.latest_status}
                            </span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 space-y-2">
                          <p>Latest Update: {new Date(task.latest_update).toLocaleDateString()}</p>
                          
                          {/* Training Details for Task 3 */}
                          {task.task_no === 3 && cellStatus?.trainingDetails && cellStatus.trainingDetails.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-blue-600 font-medium mb-1">Trainings Completed:</p>
                              <div className="flex flex-wrap gap-2">
                                {cellStatus.trainingDetails.map((training, idx) => {
                                  const trainingType = training.training_type || '';
                                  const isTrainingA = trainingType.toLowerCase().includes('training-a') || trainingType.toLowerCase().includes('training a');
                                  const isTrainingB = trainingType.toLowerCase().includes('training-b') || trainingType.toLowerCase().includes('training b');
                                  const isTrainingC = trainingType.toLowerCase().includes('training-c') || trainingType.toLowerCase().includes('training c');
                                  
                                  let label = '';
                                  if (isTrainingA) label = 'Training-A';
                                  else if (isTrainingB) label = 'Training-B';
                                  else if (isTrainingC) label = 'Training-C';
                                  else label = trainingType;
                                  
                                  return (
                                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                      {label}: {training.count}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Task Remarks */}
                          {cellStatus?.taskRemarks && (() => {
                            const taskRemark = cellStatus.taskRemarks.find(tr => tr.task_no === task.task_no);
                            if (taskRemark && taskRemark.remarks) {
                              return (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <p className="text-gray-500 text-xs font-medium mb-1">Remarks:</p>
                                  <p className="text-gray-700 text-xs">{taskRemark.remarks}</p>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status Distribution Chart */}
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <PieChart className="h-5 w-5 mr-2" />
                    Status Distribution
                  </h3>
                  <div className="space-y-4">
                    {cellStatus?.statusDistribution?.map((status) => {
                      const statusColor = getStatusBarColor(status.status);
                      return (
                        <div key={status.status} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-3" 
                              style={{ backgroundColor: statusColor }}
                            ></div>
                            <span className="text-sm font-medium text-gray-700">{status.status}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full"
                                style={{ 
                                  width: `${(status.count / status.total) * 100}%`,
                                  backgroundColor: statusColor
                                }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">{status.count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Task and Subtask Breakdown */}
                  {taskSubtaskBreakdown.length > 0 && (
                    <>
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                          <BarChart3 className="h-5 w-5 mr-2" />
                          Task & Subtask Breakdown
                        </h3>
                        <div className="space-y-4">
                          {taskSubtaskBreakdown.map((task) => (
                            <div key={task.taskNo} className="border rounded-lg p-4 relative" style={{ overflow: 'visible' }}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900">
                                  <span style={{ color: getTaskColor(task.taskNo), fontWeight: '600' }}>Task {task.taskNo}</span>: {task.taskName}
                                </h4>
                                <div className="flex items-center gap-4">
                                  <span className="text-sm font-medium text-gray-600">
                                    Total: {task.total} subtasks
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    Progress: {task.total > 0 ? Math.round((task.completed / task.total) * 100) : 0}%
                                  </span>
                                </div>
                              </div>
                              {/* Stacked Bar Chart for this task */}
                              <div className="space-y-2 relative">
                                <div className="flex items-center space-x-2 relative">
                                  <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                                    {/* Completed */}
                                    {task.completed > 0 && (
                                      <div
                                        className="absolute left-0 h-full flex items-center justify-center text-white text-xs font-semibold cursor-pointer transition-opacity hover:opacity-90"
                                        style={{
                                          width: `${(task.completed / task.total) * 100}%`,
                                          backgroundColor: '#10b981'
                                        }}
                                        onMouseEnter={() => setHoveredSegment({ taskNo: task.taskNo, status: 'completed' })}
                                        onMouseLeave={() => setHoveredSegment(null)}
                                      >
                                        {task.completed > 0 && (task.completed / task.total) * 100 > 10 && task.completed}
                                      </div>
                                    )}
                                    {/* In Progress */}
                                    {task.inProgress > 0 && (
                                      <div
                                        className="absolute h-full flex items-center justify-center text-white text-xs font-semibold cursor-pointer transition-opacity hover:opacity-90"
                                        style={{
                                          left: `${(task.completed / task.total) * 100}%`,
                                          width: `${(task.inProgress / task.total) * 100}%`,
                                          backgroundColor: 'rgb(204, 93, 232)'
                                        }}
                                        onMouseEnter={() => setHoveredSegment({ taskNo: task.taskNo, status: 'inProgress' })}
                                        onMouseLeave={() => setHoveredSegment(null)}
                                      >
                                        {task.inProgress}
                                      </div>
                                    )}
                                    {/* Not Started */}
                                    {task.notStarted > 0 && (
                                      <div
                                        className="absolute h-full flex items-center justify-center text-gray-700 text-xs font-semibold cursor-pointer transition-opacity hover:opacity-90"
                                        style={{
                                          left: `${((task.completed + task.inProgress) / task.total) * 100}%`,
                                          width: `${(task.notStarted / task.total) * 100}%`,
                                          backgroundColor: '#9ca3af'
                                        }}
                                        onMouseEnter={() => setHoveredSegment({ taskNo: task.taskNo, status: 'notStarted' })}
                                        onMouseLeave={() => setHoveredSegment(null)}
                                      >
                                        {task.notStarted > 0 && (task.notStarted / task.total) * 100 > 10 && task.notStarted}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {/* Tooltip for subtasks */}
                                {hoveredSegment && hoveredSegment.taskNo === task.taskNo && (
                                  <div className="absolute z-50 bg-white border border-gray-300 rounded-lg shadow-xl p-4 mt-2 min-w-[300px] max-w-[500px] max-h-[400px] flex flex-col">
                                    <h5 className="font-semibold text-gray-900 mb-2 text-sm flex-shrink-0">
                                      {hoveredSegment.status === 'completed' && 'Completed Subtasks'}
                                      {hoveredSegment.status === 'inProgress' && 'In Progress Subtasks'}
                                      {hoveredSegment.status === 'notStarted' && 'Not Started Subtasks'}
                                    </h5>
                                    <div className="space-y-1 overflow-y-auto overflow-x-hidden flex-1 pr-2" style={{ maxHeight: '300px', scrollbarWidth: 'thin' }}>
                                      {(hoveredSegment.status === 'completed' && task.completedSubtasks.length > 0) && (
                                        task.completedSubtasks.map((subtask, idx) => (
                                          <div key={idx} className="text-xs text-gray-700 py-1.5 border-b border-gray-100 last:border-b-0 pr-2">
                                            {subtask}
                                          </div>
                                        ))
                                      )}
                                      {(hoveredSegment.status === 'inProgress' && task.inProgressSubtasks.length > 0) && (
                                        task.inProgressSubtasks.map((subtask, idx) => (
                                          <div key={idx} className="text-xs text-gray-700 py-1.5 border-b border-gray-100 last:border-b-0 pr-2">
                                            {subtask}
                                          </div>
                                        ))
                                      )}
                                      {(hoveredSegment.status === 'notStarted' && task.notStartedSubtasks.length > 0) && (
                                        task.notStartedSubtasks.map((subtask, idx) => (
                                          <div key={idx} className="text-xs text-gray-700 py-1.5 border-b border-gray-100 last:border-b-0 pr-2">
                                            {subtask}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                )}
                                {/* Legend for this task */}
                                <div className="flex flex-wrap gap-3 text-xs">
                                  {task.completed > 0 && (
                                    <div className="flex items-center">
                                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: '#10b981' }}></div>
                                      <span className="text-gray-700">Completed: {task.completed}</span>
                                    </div>
                                  )}
                                  {task.inProgress > 0 && (
                                    <div className="flex items-center">
                                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: 'rgb(204, 93, 232)' }}></div>
                                      <span className="text-gray-700">In Progress: {task.inProgress}</span>
                                    </div>
                                  )}
                                  {task.notStarted > 0 && (
                                    <div className="flex items-center">
                                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: '#9ca3af' }}></div>
                                      <span className="text-gray-700">Not Started: {task.notStarted}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Detailed Task Progress */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Detailed Task Progress
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b-2 border-gray-300 bg-gray-50">
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Task</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Subtask</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Status</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Progress</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Remarks</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Last Updated</th>
                        <th className="py-3 px-4 font-semibold text-gray-700">Submitted By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Apply filters to unique subtasks
                        const filteredData = uniqueDetailedProgress.filter(item => {
                          const matchesStatus = !statusFilter || item.status === statusFilter;
                          const matchesSearch = !searchTerm || 
                            item.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.subtask_name.toLowerCase().includes(searchTerm.toLowerCase());
                          return matchesStatus && matchesSearch;
                        });
                        
                        const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                        
                        return paginatedData.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-3 px-4 border-r border-gray-200">
                            <span style={{ color: getTaskColor(item.task_no), fontWeight: '600' }}>Task {item.task_no}</span>: {item.task_name}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">Subtask {item.subtask_no}: {item.subtask_name}</td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                              {getStatusIcon(item.status)} {item.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            <div className="flex items-center space-x-2">
                              {(() => {
                                const statusLower = (item.status || '').toLowerCase().trim().replace(/-/g, ' ');
                                const displayProgress = statusLower === 'completed' ? 100 : (item.progress || 0);
                                return (
                                  <>
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="h-2 rounded-full"
                                        style={{ 
                                          width: `${displayProgress}%`,
                                          backgroundColor: getStatusBarColor(item.status)
                                        }}
                                      ></div>
                                    </div>
                                    <span className="text-xs text-gray-600">{displayProgress}%</span>
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">
                            {item.remarks ? (
                              <span className="text-gray-700">{item.remarks}</span>
                            ) : (
                              <span className="text-gray-400 italic">No remarks</span>
                            )}
                          </td>
                          <td className="py-3 px-4 border-r border-gray-200">{new Date(item.last_updated).toLocaleDateString()}</td>
                          <td className="py-3 px-4">{item.submitted_by}</td>
                        </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                  
                  {/* Pagination */}
                  {(() => {
                    // Apply filters to unique subtasks
                    const filteredData = uniqueDetailedProgress.filter(item => {
                      const matchesStatus = !statusFilter || item.status === statusFilter;
                      const matchesSearch = !searchTerm || 
                        item.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.subtask_name.toLowerCase().includes(searchTerm.toLowerCase());
                      return matchesStatus && matchesSearch;
                    });
                    
                    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
                    
                    if (totalPages > 1) {
                      return (
                        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-gray-50 border-t border-gray-200">
                          <div className="text-sm text-gray-700">
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} results
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1 text-sm">
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CellStatus;
